import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Autocomplete,
  Switch,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import { createSubUser } from "../../../api/user/createSubUser";
import { updateSubUser } from "../../../api/user/updateSubUser";
import Swal from "sweetalert2";

const SubUserProcess = ({
  open,
  onClose,
  process,
  userData = null,
  taxpayerList = [],
  search,
}) => {
  // Form states
  const [email, setEmail] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectAllTaxpayers, setSelectAllTaxpayers] = useState(false);
  const [selectedTaxpayers, setSelectedTaxpayers] = useState([]);

  // Form validation states
  const [emailError, setEmailError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [taxpayerError, setTaxpayerError] = useState("");

  // Determine if password is required based on process type
  const isPasswordRequired = process === "add";

  useEffect(() => {
    if (process === "update" && userData) {
      // Pre-fill form with user data for update mode
      setEmail(userData.email || "");
      setUsername(userData.username || "");
      setIsActive(userData.status || false); // varsa status alanı
      // Password is not pre-filled in update mode
      setPassword("");

      // Handle taxpayer selection
      if (userData.taxpayerIds && userData.taxpayerIds.includes("all")) {
        setSelectAllTaxpayers(true);
        setSelectedTaxpayers([]);
      } else if (userData.taxpayerIds && userData.taxpayerIds.length > 0) {
        console.log(userData);
        // Find taxpayer objects from the list based on IDs
        const selectedTaxpayerObjects = taxpayerList.filter((taxpayer) =>
          userData.taxpayerIds.includes(taxpayer.id)
        );
        setSelectedTaxpayers(selectedTaxpayerObjects);
        setSelectAllTaxpayers(false);
      }
    } else {
      // Reset form for add mode
      resetForm();
    }
  }, [process, userData, taxpayerList, open]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleAllTaxpayersChange = (event) => {
    setSelectAllTaxpayers(event.target.checked);
    if (event.target.checked) {
      setSelectedTaxpayers([]);
      setTaxpayerError("");
    }
  };

  const handleTaxpayerChange = (event, newValue) => {
    setSelectedTaxpayers(newValue);
    if (newValue.length > 0) {
      setTaxpayerError("");
      setSelectAllTaxpayers(false);
    } else if (!selectAllTaxpayers) {
      setTaxpayerError(
        "Lütfen en az bir mükellef seçin veya tüm mükelleflere yetki verin"
      );
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character
    const re =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/;
    return re.test(password);
  };

  const validateForm = () => {
    let isValid = true;

    if (!email) {
      setEmailError("Email alanı zorunludur");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Geçerli bir email adresi giriniz");
      isValid = false;
    } else {
      setEmailError("");
    }

    if (!username) {
      setUsernameError("Kullanıcı adı zorunludur");
      isValid = false;
    } else if (username.length < 3) {
      setUsernameError("Kullanıcı adı en az 3 karakter olmalıdır");
      isValid = false;
    } else {
      setUsernameError("");
    }

    // Password validation - only required for add mode or if provided in update mode
    if (isPasswordRequired && !password) {
      setPasswordError("Şifre alanı zorunludur");
      isValid = false;
    } else if (password && !validatePassword(password)) {
      setPasswordError(
        "Şifre en az 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter içermelidir"
      );
      isValid = false;
    } else {
      setPasswordError("");
    }

    if (!selectAllTaxpayers && selectedTaxpayers.length === 0) {
      setTaxpayerError(
        "Lütfen en az bir mükellef seçin veya tüm mükelleflere yetki verin"
      );
      isValid = false;
    } else {
      setTaxpayerError("");
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      const userDataToSubmit = {
        email,
        username,
        status: typeof isActive === "boolean" ? isActive : userData.status,
        taxpayer: selectAllTaxpayers
          ? ["all"]
          : selectedTaxpayers.map((taxpayer) => taxpayer.id),
        permission: ["full"],
      };

      // Only include password if it's provided (required for add, optional for update)
      if (password) {
        userDataToSubmit.password = password;
      }

      // Add user ID for update mode
      if (process === "update" && userData && userData.id) {
        userDataToSubmit.id = userData.id;
      }

      try {
        let response;
        if (process === "add") {
          response = await createSubUser(userDataToSubmit);
        } else {
          response = await updateSubUser(userDataToSubmit);
        }
        search();
        Swal.fire({
          text: "İşlem Başarılı",
          icon: "success",
          confirmButtonText: "Tamam",
          customClass: { container: ".swal-overlay" },
        });

        // Reset form and close dialog
        resetForm();
        onClose();
      } catch (error) {
        console.error("İşlem hatası:", error);
        Swal.fire({
          text: error.response?.data?.message || "Bir hata oluştu",
          icon: "error",
          confirmButtonText: "Tamam",
          customClass: { container: ".swal-overlay" },
        });
      }
    }
  };

  const resetForm = () => {
    setEmail("");
    setUsername("");
    setPassword("");
    setSelectAllTaxpayers(false);
    setSelectedTaxpayers([]);
    setEmailError("");
    setUsernameError("");
    setPasswordError("");
    setTaxpayerError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getTitleText = () => {
    return process === "add" ? "Yeni Kullanıcı Ekle" : "Kullanıcı Düzenle";
  };

  const getActionButtonText = () => {
    return process === "add" ? "Kullanıcı Ekle" : "Kaydet";
  };

  const getTitleIcon = () => {
    return process === "add" ? (
      <PersonAddIcon sx={{ color: "#7367f0", mr: 1 }} />
    ) : (
      <EditIcon sx={{ color: "#7367f0", mr: 1 }} />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {getTitleIcon()}
          <Typography variant="h6" fontWeight="bold">
            {getTitleText()}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box
          component="form"
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <TextField
            required
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            autoComplete="email"
          />

          <TextField
            required
            fullWidth
            label="Kullanıcı Adı"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!usernameError}
            helperText={usernameError}
          />

          <TextField
            required={isPasswordRequired}
            fullWidth
            label={
              isPasswordRequired ? "Şifre" : "Şifre (Değiştirmek için doldurun)"
            }
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAllTaxpayers}
                  onChange={handleAllTaxpayersChange}
                  sx={{
                    color: "#7367f0",
                    "&.Mui-checked": {
                      color: "#7367f0",
                    },
                  }}
                />
              }
              label="Tüm Mükelleflere Yetki Ver"
            />

            <FormControl
              fullWidth
              sx={{ mt: 2, display: selectAllTaxpayers ? "none" : "block" }}
            >
              <Autocomplete
                multiple
                id="taxpayers-autocomplete"
                options={taxpayerList}
                getOptionLabel={(option) => option.title}
                value={selectedTaxpayers}
                onChange={handleTaxpayerChange}
                noOptionsText="Mükellef bulunamadı"
                disabled={selectAllTaxpayers}
                PopperProps={{
                  placement: "bottom-start", // Listenin aşağı doğru açılmasını sağlar
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Mükellefler"
                    placeholder="Mükellef seçin"
                    error={!!taxpayerError}
                    helperText={taxpayerError}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: taxpayerError ? "#d32f2f" : undefined,
                        },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => (
                  <Box
                    sx={{
                      maxHeight: 120, // yüksekliği sınırlıyoruz
                      overflowY: "auto",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      pr: 1,
                    }}
                  >
                    {value.map((option, index) => (
                      <Chip
                        key={option.title + index}
                        label={
                          option.title.length > 20
                            ? `${option.title.substring(0, 20)}...`
                            : option.title
                        }
                        {...getTagProps({ index })}
                        sx={{
                          backgroundColor: "rgba(115, 103, 240, 0.1)",
                          color: "#7367f0",
                        }}
                        title={option.title}
                      />
                    ))}
                  </Box>
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.title.length > 20
                      ? `${option.title.substring(0, 20)}...`
                      : option.title}
                  </li>
                )}
                ChipProps={{
                  deleteIcon: <CloseIcon fontSize="small" />,
                }}
                sx={{
                  "& .MuiAutocomplete-tag": {
                    margin: "3px",
                  },
                  "& .MuiChip-deleteIcon": {
                    color: "#7367f0",
                    "&:hover": {
                      color: "#5e50e9",
                    },
                  },
                }}
              />
            </FormControl>
            {process === "update" && (
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={isActive ? "green" : "error"}
                    >
                      {isActive ? "Aktif" : "Pasif"}
                    </Typography>
                  }
                />
              </FormControl>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: "#7367f0",
            color: "#7367f0",
            "&:hover": {
              borderColor: "#5e50e9",
              backgroundColor: "rgba(115, 103, 240, 0.04)",
            },
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            bgcolor: "#7367f0",
            "&:hover": {
              bgcolor: "#5e50e9",
            },
            boxShadow: "0 2px 4px rgba(115,103,240,0.3)",
          }}
        >
          {getActionButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubUserProcess;
